/* Operation Outbreak — hero contagion network.
   Extracted verbatim from site.js; behaviour is unchanged.

   INIT CONTRACT
   - Mount:      the first <canvas class="net"> in the document. No canvas, no work:
                 the module returns immediately, so it is safe on every page.
   - Zone:       sizing and pointer events follow the canvas's nearest .netzone,
                 falling back to .hero, then the canvas's parent.
   - Colours:    read at runtime from CSS custom properties on <html> via
                 getComputedStyle, so the arm palette and any theme change apply
                 without touching this file.
   - Motion:     honours prefers-reduced-motion — when set, the field paints one
                 static frame and no animation loop starts. Viewports 700px and
                 under skip the field entirely (canvas is also hidden in CSS).
   - API:        publishes window.__ooNet = { repaint(), destroy() }.
                 repaint()  re-reads the CSS palette and redraws (site.js calls this
                            when the audience arm changes; the call is guarded, so
                            load order between the two files does not matter).
                 destroy()  cancels the frame loop and detaches every listener.
   - Load:       after site.css (it reads computed custom properties) and only on
                 pages that contain canvas.net. Order relative to site.js is free.
*/
export default function contagion() {
  /* ---- hero contagion network ----
     Nodes drift freely at all times. Lines appear when two pass within LINK.
     Click one (or wait for a random seeding) and it turns red: infected. Anything
     susceptible it touches catches it, and those links turn red too. Infected
     recover to blue, become immune for a while, then fade back to grey and can
     catch it again. One O(N^2) pass per frame at N=72 keeps it cheap. */
  var canvas = document.querySelector('canvas.net');
  if (canvas) {
    if (matchMedia('(max-width: 700px)').matches) {
      return;
    }
    var hero = canvas.closest('.netzone') || canvas.closest('.hero') || canvas.parentElement;
    var calm = matchMedia('(prefers-reduced-motion: reduce)').matches;
    var ctx = canvas.getContext('2d');
    var N = (document.body.dataset.theme === 'home' ? 190 : 60), LINK = 128, R_INFECT = 60, P_INFECT = 0.6, T_I = 6.0, T_R = 7.5, SEED_EVERY = 3, CFR = 0.20;
    var W = 0, H = 0, nodes = [], rings = [];
    var DPR = Math.min(devicePixelRatio || 1, 2);
    /* client coordinates, resolved to canvas space at paint time (see syncPtr) */
    var ptr = { cx: 0, cy: 0, x: 0, y: 0, on: false };

    /* the hero copy is a keep-out zone so text never competes with the crowd */
    var keeps = [];
    var KPAD = 10, KPADX = 4;
    function boxFor(el, pad) {
      if (!el) { return null; }
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) { return null; }
      var c = canvas.getBoundingClientRect();
      var px = KPADX, py = pad;
      return { x0: r.left - c.left - px, y0: r.top - c.top - py, x1: r.right - c.left + px, y1: r.bottom - c.top + py };
    }
    function measureKeep() {
      var SEL = ".home .htxt .h-xl, .home .htxt .lead, .heroask-q, .heroask-n, #mission .eyebrow, #mission h2, #mission p, #mission .misstats .n, #mission .misstats .l, #story .eyebrow, #story h2, #story p, #story .btn, .sechead--offer .eyebrow, .techlead h2, .techlead p, #coverage .eyebrow, #coverage h2, #coverage .btn, #funders .eyebrow, #funders h2, #funders .lead, .hero .htxt .eyebrow, .hero .htxt .h-xl, .hero .htxt .lead, .ctaband h2, .ctaband .body, .ctaband .lead, .ctaband .btn";
      // Array.from/slice, not spread: buble compiles `[...querySelectorAll()]` to
      // `[].concat(nodeList)`, which wraps the NodeList and then boxFor throws.
      var boxes = Array.prototype.slice.call(hero.querySelectorAll(SEL))
        .map(function (el) { return boxFor(el, KPAD); })
        .filter(Boolean)
        .filter(function (k) { return (k.x1 - k.x0) * (k.y1 - k.y0) <= W * H * 0.94; });
      /* merge boxes that overlap or sit within a line of each other, so tight runs
         (eyebrow over heading, paragraph breaks) read as one block */
      var GAP = 14;
      var merged = true;
      while (merged) {
        merged = false;
        outer: for (var a = 0; a < boxes.length; a++) {
          for (var b = a + 1; b < boxes.length; b++) {
            var A = boxes[a], B = boxes[b];
            var overlapX = A.x0 - GAP < B.x1 && B.x0 - GAP < A.x1;
            var overlapY = A.y0 - GAP < B.y1 && B.y0 - GAP < A.y1;
            if (!overlapX || !overlapY) { continue; }
            boxes[a] = { x0: Math.min(A.x0, B.x0), y0: Math.min(A.y0, B.y0), x1: Math.max(A.x1, B.x1), y1: Math.max(A.y1, B.y1) };
            boxes.splice(b, 1); merged = true; break outer;
          }
        }
      }
      keeps = boxes;
    }
    function evict(n) {
      for (var i = 0, list = keeps; i < list.length; i += 1) {
        var k = list[i];

        var r = n.r || 3;
        if (n.x + r < k.x0 || n.x - r > k.x1 || n.y + r < k.y0 || n.y - r > k.y1) { continue; }
        /* push to the nearest edge of the guard band and reflect, so the dot bounces
           off the text box instead of drifting through it */
        var dl = (n.x + r) - k.x0, dr = k.x1 - (n.x - r);
        var dt = (n.y + r) - k.y0, db = k.y1 - (n.y - r);
        var m = Math.min(dl, dr, dt, db);
        if (m === dl) { n.x = k.x0 - r; if (n.vx > 0) { n.vx = -n.vx; } }
        else if (m === dr) { n.x = k.x1 + r; if (n.vx < 0) { n.vx = -n.vx; } }
        else if (m === dt) { n.y = k.y0 - r; if (n.vy > 0) { n.vy = -n.vy; } }
        else { n.y = k.y1 + r; if (n.vy < 0) { n.vy = -n.vy; } }
      }
    }
    var inKeep = function (x, y) { for (var i = 0, list = keeps; i < list.length; i += 1) {
      var k = list[i];

      if (x > k.x0 && x < k.x1 && y > k.y0 && y < k.y1) { return true;
    } } return false; };

    function pal() {
      var cs = getComputedStyle(document.documentElement);
      return {
        s: '#5A6266',
        i: '#CE2F2F',
        r: (cs.getPropertyValue('--oo-indigo') || '#5A65E5').trim(),
        edge: 'rgba(58,62,63,.42)',
        hot: 'rgba(206,47,47,.62)',
        halo: 'rgba(206,47,47,.13)',
        rRing: 'rgba(90,101,229,.42)',
        dead: 'rgba(90,98,102,.55)',
        ac: (cs.getPropertyValue('--arm') || '#5A65E5').trim()
      };
    }
    var C = pal();

    /* the Visualizer's infected mark: a four-point star with pinched arms */
    function sparkle(x, y, R, fill) {
      var w = R * .17;
      ctx.fillStyle = fill;
      ctx.beginPath();
      ctx.moveTo(x + R, y);
      for (var i = 0; i < 4; i++) {
        var a0 = i * 1.5708, a1 = a0 + 1.5708, am = a0 + .7854;
        ctx.quadraticCurveTo(x + Math.cos(am) * w, y + Math.sin(am) * w, x + Math.cos(a1) * R, y + Math.sin(a1) * R);
      }
      ctx.closePath();
      ctx.fill();
    }

    function resize() {
      var r = canvas.getBoundingClientRect();
      var pw = W, ph = H;
      W = r.width; H = r.height || 600;
      canvas.width = W * DPR; canvas.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      measureKeep();
      if (!nodes.length) { seed(); }
      else if (pw > 0 && ph > 0 && (pw !== W || ph !== H)) {
        var sx = W / pw, sy = H / ph;
        for (var i = 0, list = nodes; i < list.length; i += 1) { var n = list[i];

          n.x *= sx; n.y *= sy; }
      }
    }
    function seed() {
      nodes = Array.from({ length: N }, function () { return ({
        x: Math.random() * W, y: Math.random() * H,
        vx: (Math.random() - .5) * 20, vy: (Math.random() - .5) * 20,
        st: 0, t: 0, wob: Math.random() * 6.28
      }); });
    }
    function infect(n) { if (n.st !== 0) { return; } n.st = 1; n.t = 0; }
    var alive = function (n) { return n.st !== 3; };

    /* teardown handles: destroy() aborts every listener, the loop, and the observer */
    var ac = new AbortController(), sig = ac.signal;
    var raf = 0, ro = null, dead = false;

    if (!calm) {
      hero.addEventListener('pointermove', function (e) {
        ptr.cx = e.clientX; ptr.cy = e.clientY; ptr.on = true;
      }, { passive: true, signal: sig });
      hero.addEventListener('pointerleave', function () { ptr.on = false; }, { signal: sig });
      hero.addEventListener('pointerdown', function (e) {
        ptr.cx = e.clientX; ptr.cy = e.clientY; ptr.on = true;
        var r = canvas.getBoundingClientRect();
        var px = e.clientX - r.left, py = e.clientY - r.top;
        var best = null, bd = 1e9;
        for (var i = 0, list = nodes; i < list.length; i += 1) { var n = list[i];

          if (!alive(n)) { continue; } var d = Math.hypot(n.x - px, n.y - py); if (d < bd) { bd = d; best = n; } }
        if (best && best.st !== 3) { best.st = 1; best.t = 0; rings.push({ x: best.x, y: best.y, t: 0 }); }
        if (e.pointerType === 'touch') { ptr.on = false; }
      }, { passive: true, signal: sig });
    }

    var prev = performance.now(), since = 0;
    function frame(now) {
      if (dead) { return; }
      var dt = Math.min((now - prev) / 1000, .05); prev = now; since += dt;
      ctx.clearRect(0, 0, W, H);

      /* drift: a slow wander on top of constant velocity, so paths look organic */
      for (var i$2 = 0, list = nodes; i$2 < list.length; i$2 += 1) {
        var n = list[i$2];

        n.wob += dt * .7;
        n.vx += Math.cos(n.wob) * 5 * dt;
        n.vy += Math.sin(n.wob * 1.3) * 5 * dt;
        var sp = Math.hypot(n.vx, n.vy);
        if (sp > 26) { n.vx *= 26 / sp; n.vy *= 26 / sp; }
        else if (sp < 7) { var k = 7 / (sp || 1); n.vx *= k; n.vy *= k; }
        n.x += n.vx * dt; n.y += n.vy * dt;
        if (n.x < 0) { n.x = 0; n.vx = Math.abs(n.vx); }
        else if (n.x > W) { n.x = W; n.vx = -Math.abs(n.vx); }
        if (n.y < 0) { n.y = 0; n.vy = Math.abs(n.vy); }
        else if (n.y > H) { n.y = H; n.vy = -Math.abs(n.vy); }
        evict(n);
        n.t += dt;
        if (n.st === 1 && n.t > T_I) {
          if (Math.random() < CFR) { n.st = 3; n.t = 0; }
          else { n.st = 2; n.t = 0; }
        }
        else if (n.st === 2 && n.t > T_R) { n.st = 0; n.t = 0; }
        else if (n.st === 3 && n.t > 0.9) {
          /* respawn somewhere else as susceptible — the room stays the same size */
          n.st = 0; n.t = 0;
          n.x = Math.random() * W; n.y = Math.random() * H;
          n.vx = (Math.random() - .5) * 20; n.vy = (Math.random() - .5) * 20;
          evict(n);
        }
      }

      /* one pass: draw contacts and transmit across them */
      ctx.lineWidth = 1;
      for (var i = 0; i < N; i++) {
        var a = nodes[i];
        for (var k$1 = i + 1; k$1 < N; k$1++) {
          var b = nodes[k$1];
          if (a.st === 3 || b.st === 3) { continue; }
          var dx = a.x - b.x, dy = a.y - b.y, d = Math.hypot(dx, dy);
          if (d > LINK) { continue; }
          var hot = a.st === 1 || b.st === 1;
          if (!inKeep((a.x + b.x) / 2, (a.y + b.y) / 2)) {
            ctx.strokeStyle = hot ? C.hot : C.edge;
            ctx.globalAlpha = .35 + .65 * (1 - d / LINK);
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
            ctx.globalAlpha = 1;
          }
          if (hot && d < R_INFECT && Math.random() < P_INFECT * dt) { infect(a.st === 1 ? b : a); }
        }
      }

      /* the pointer is the pathogen: a slowly tumbling virion.
         Its position is re-resolved from client coordinates every frame, so a layout
         shift under a still cursor (arm toggle, reveal, scroll) cannot separate them. */
      if (ptr.on) {
        var pr = canvas.getBoundingClientRect();
        ptr.x = ptr.cx - pr.left; ptr.y = ptr.cy - pr.top;
        if (ptr.x < -40 || ptr.y < -40 || ptr.x > pr.width + 40 || ptr.y > pr.height + 40) { ptr.on = false; }
      }
      if (ptr.on) {
        var spin = now / 1400;
        ctx.strokeStyle = C.i; ctx.fillStyle = C.i;
        ctx.globalAlpha = .8; ctx.lineWidth = 1.1;
        for (var k$2 = 0; k$2 < 12; k$2++) {
          var ang = spin + k$2 * 6.284 / 12;
          ctx.beginPath();
          ctx.moveTo(ptr.x + Math.cos(ang) * 5.2, ptr.y + Math.sin(ang) * 5.2);
          ctx.lineTo(ptr.x + Math.cos(ang) * 9.4, ptr.y + Math.sin(ang) * 9.4);
          ctx.stroke();
          ctx.beginPath(); ctx.arc(ptr.x + Math.cos(ang) * 9.4, ptr.y + Math.sin(ang) * 9.4, 1.5, 0, 6.284); ctx.fill();
        }
        ctx.globalAlpha = .95;
        ctx.beginPath(); ctx.arc(ptr.x, ptr.y, 5.2, 0, 6.284); ctx.fill();
        ctx.globalAlpha = 1;
      }

      /* click ripples */
      for (var i$1 = rings.length - 1; i$1 >= 0; i$1--) {
        var r = rings[i$1]; r.t += dt;
        var alpha = 1 - r.t / .85;
        if (alpha <= 0) { rings.splice(i$1, 1); continue; }
        ctx.strokeStyle = C.i; ctx.globalAlpha = alpha * .5; ctx.lineWidth = 1.6;
        ctx.beginPath(); ctx.arc(r.x, r.y, r.t * 300, 0, 6.284); ctx.stroke();
        ctx.globalAlpha = 1;
      }

      /* node marks follow the Outbreak Visualizer: a plain dot while susceptible,
         a four-point sparkle once infected, a ringed dot once recovered */
      for (var i$3 = 0, list$1 = nodes; i$3 < list$1.length; i$3 += 1) {
        var n$1 = list$1[i$3];

        if (n$1.st === 3) {
          /* the sparkle collapses and greys out, then the slot respawns */
          var k$3 = Math.max(1 - n$1.t / 0.9, 0);
          ctx.globalAlpha = k$3;
          sparkle(n$1.x, n$1.y, 2.4 + 6.4 * k$3, C.dead);
          ctx.globalAlpha = 1;
          continue;
        }
        if (n$1.st === 1) {
          /* two stacked translucent discs stand in for a bloom, at a fraction of the cost */
          ctx.fillStyle = C.halo;
          ctx.beginPath(); ctx.arc(n$1.x, n$1.y, 14, 0, 6.284); ctx.fill();
          ctx.beginPath(); ctx.arc(n$1.x, n$1.y, 8, 0, 6.284); ctx.fill();
          sparkle(n$1.x, n$1.y, 8.2 + .8 * Math.sin(now / 240 + n$1.x * .05), C.i);
          continue;
        }
        if (n$1.st === 2) {
          ctx.strokeStyle = C.rRing; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.arc(n$1.x, n$1.y, 6.6, 0, 6.284); ctx.stroke();
          ctx.fillStyle = C.r;
          ctx.beginPath(); ctx.arc(n$1.x, n$1.y, 3.3, 0, 6.284); ctx.fill();
          continue;
        }
        ctx.fillStyle = C.s;
        ctx.beginPath(); ctx.arc(n$1.x, n$1.y, 3.6, 0, 6.284); ctx.fill();
      }

      /* an occasional spontaneous case keeps the room alive on its own */
      if (since > SEED_EVERY) {
        since = 0;
        infect(nodes[(Math.random() * N) | 0]);
      }
      raf = requestAnimationFrame(frame);
    }

    window.__ooNet = {
      /* stops the loop and detaches every listener; the last painted frame stays on screen */
      destroy: function destroy() {
        dead = true;
        cancelAnimationFrame(raf);
        ac.abort();
        if (ro) { ro.disconnect(); }
      },
      repaint: function repaint() {
        C = pal();
        /* the arm's copy differs in length, so the hero resizes on the frame after the
           switch — resize twice, once now and once after layout has settled */
        requestAnimationFrame(function () { resize(); requestAnimationFrame(resize); });
      },
      /* a deliberate index case: infect the nearest susceptible to a point given in
         fractions of the field, and push a ripple so the event is visible */
      ignite: function ignite(fx, fy) {
        if (!nodes.length) { return; }
        var x = (fx == null ? .5 : fx) * W, y = (fy == null ? .3 : fy) * H;
        var best = null, bd = 1e9;
        for (var i = 0, list = nodes; i < list.length; i += 1) { var n = list[i];

          if (n.st !== 0) { continue; } var d = Math.hypot(n.x - x, n.y - y); if (d < bd) { bd = d; best = n; } }
        if (!best) { return; }
        best.st = 1; best.t = 0;
        rings.push({ x: best.x, y: best.y, t: 0 });
      }
    };
    addEventListener('resize', resize, { signal: sig });
    var rq = 0, rz = 0;
    var scheduleMeasure = function () { if (rq) { return; } rq = requestAnimationFrame(function () { rq = 0; measureKeep(); }); };
    var scheduleResize = function () { if (rz) { return; } rz = requestAnimationFrame(function () { rz = 0; resize(); }); };
    window.addEventListener('scroll', scheduleMeasure, { passive: true, signal: sig });
    /* the hero's own box changing is a resize, not just a reflow of the text within it */
    if (window.ResizeObserver) { ro = new ResizeObserver(scheduleResize); ro.observe(hero); }
    setTimeout(measureKeep, 400); setTimeout(measureKeep, 1400);
    resize();
    frame(performance.now());
    addEventListener('load', function () { resize(); }, { signal: sig });
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(function () { return resize(); }); }
  }
}
